import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Container,
  Row,
  Col,
  FormControl,
  InputGroup,
  Button,
  Spinner,
  Card,
  Table,
  Badge,
} from 'react-bootstrap';
import Loading from '../shared/loading/Loading';
import { useConfirmationDialog } from '../../hooks/useConfirmationDialog';
import { useLoading } from '../../hooks/useLoading';
import { loadConfigurationFactors } from '../../api/configuration';
import { Quote, loadQuotes } from '../../api/quotations';
import {
  convertQuoteServer,
  deleteQuoteServer,
} from '../../api/quotationsServer';
import {
  Search as FiSearch,
  Plus as FiPlus,
} from 'react-bootstrap-icons';
import listStyles from '../shared/layout/dataListTable.module.scss';
import styles from './Quotations.module.scss';
import QuotationForm from './QuotationForm';
import QuotationView from './QuotationView';
import QuotationRowActions from './QuotationRowActions';
import { getQuoteReference } from './quoteReference';
import {
  resolveOrderCreatorDisplay,
  pascalCaseWords,
  type OrderCreatorDisplayInfo,
} from '../orders/orderCreatorDisplay';
import SubscriptionGuard from '../shared/billing/SubscriptionGuard';
import { Organization, SUPPORTED_CURRENCIES } from '../../models/organization';
import {
  formatDateEsFromFirestore,
  formatTimeEsFromFirestore,
} from '../../utils/format';
import { PANEL_PERMISSIONS } from '../../utils/panelPermissions';
import {
  PanelReadOnlyBanner,
  RequirePanelWrite,
} from '../auth/PanelWriteGate';
import { useCanWritePanelArea } from '../../contexts/PanelAccessContext';

interface QuotationsProps {
  device: string;
  addToast: (message: string) => void;
  navStatus: string;
  organizationId: string;
  organization: Organization | null;
}

export default function Quotations({
  device,
  addToast,
  navStatus,
  organizationId,
  organization,
}: QuotationsProps) {
  const { showConfirmation } = useConfirmationDialog();
  const { showLoading, hideLoading } = useLoading();
  const canWriteQuotations = useCanWritePanelArea(
    PANEL_PERMISSIONS.QUOTATIONS
  );
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [search, setSearch] = useState('');
  const [pending, setPending] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [quoteToEdit, setQuoteToEdit] = useState<Quote | null>(null);
  const [showQuoteView, setShowQuoteView] = useState(false);
  const [selectedQuoteForView, setSelectedQuoteForView] =
    useState<Quote | null>(null);
  const [referenceCurrency, setReferenceCurrency] = useState('USD');
  const [loadMore, setLoadMore] = useState(false);
  const [lastDocument, setLastDocument] = useState<any>(null);
  const [loadMorePending, setLoadMorePending] = useState(false);
  const [creatorDisplayByStored, setCreatorDisplayByStored] = useState<
    Record<string, OrderCreatorDisplayInfo>
  >({});

  const loadQuotesRequestIdRef = useRef(0);
  const mountedRef = useRef(true);
  const creatorDisplayFetchStartedRef = useRef<Set<string>>(new Set());

  const uniqueCreatorKeys = useMemo(
    () =>
      Array.from(
        new Set(
          quotes
            .map(q => (q.creatorSalesPerson || '').trim())
            .filter(Boolean)
        )
      ),
    [quotes]
  );

  const renderCreatorSalesPerson = useCallback(
    (raw: string | undefined) => {
      const s = (raw ?? '').trim();
      if (!s) return '—';
      const info = creatorDisplayByStored[s];
      if (!info) return pascalCaseWords(s);
      return (
        <span className="d-inline-flex align-items-center flex-wrap gap-1">
          <span>{info.name}</span>
          {info.isAdmin && (
            <Badge bg="secondary" className="small">
              Admin
            </Badge>
          )}
        </span>
      );
    },
    [creatorDisplayByStored]
  );

  const groupedNumberFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const getCurrencySymbol = useCallback((code?: string): string => {
    if (!code) return '';
    const match = SUPPORTED_CURRENCIES.find(curr => curr.code === code);
    return match?.symbol || code;
  }, []);
  const formatMoney = useCallback(
    (amount: number, code: string = referenceCurrency): string => {
      const rounded = groupedNumberFormatter.format(Number(amount) || 0);
      const symbol = getCurrencySymbol(code);
      return symbol ? `${symbol} ${rounded}` : `${rounded}`;
    },
    [referenceCurrency, getCurrencySymbol]
  );

  const loadConfiguration = useCallback(async () => {
    try {
      const config = await loadConfigurationFactors(organizationId);
      setReferenceCurrency(config.referenceCurrency || 'USD');
    } catch (e) {
      console.error('Failed to load configuration:', e);
    }
  }, [organizationId]);

  const loadQuotesList = useCallback(async () => {
    if (!organizationId) return;
    const requestId = ++loadQuotesRequestIdRef.current;
    setPending(true);
    try {
      const result = await loadQuotes(
        organizationId,
        search || '',
        undefined,
        10
      );
      if (requestId !== loadQuotesRequestIdRef.current) return;
      setQuotes(result.quotes);
      setLoadMore(result.hasMore);
      setLastDocument(result.lastDoc);
    } catch (error) {
      if (requestId !== loadQuotesRequestIdRef.current) return;
      console.log(error);
      addToast('Error al cargar cotizaciones');
      setLoadMore(false);
    } finally {
      if (requestId === loadQuotesRequestIdRef.current) {
        setPending(false);
      }
    }
  }, [organizationId, search, addToast]);

  const getQuotes = useCallback(() => {
    void loadQuotesList();
  }, [loadQuotesList]);

  useEffect(() => {
    mountedRef.current = true;
    setIsLoading(true);
    void loadConfiguration().finally(() => {
      if (mountedRef.current) setIsLoading(false);
    });
    return () => {
      mountedRef.current = false;
    };
  }, [loadConfiguration]);

  useEffect(() => {
    getQuotes();
  }, [getQuotes]);

  useEffect(() => {
    uniqueCreatorKeys.forEach(key => {
      if (creatorDisplayFetchStartedRef.current.has(key)) return;
      creatorDisplayFetchStartedRef.current.add(key);
      void resolveOrderCreatorDisplay(organizationId, key).then(info => {
        if (!mountedRef.current || !info) return;
        setCreatorDisplayByStored(prev => ({ ...prev, [key]: info }));
      });
    });
  }, [uniqueCreatorKeys, organizationId]);

  const handleDelete = useCallback(
    (quote: Quote) => {
      if (!canWriteQuotations) return;
      showConfirmation(
        {
          title: 'Eliminar cotización',
          message: `¿Eliminar la cotización ${getQuoteReference(quote)} de ${
            quote.clientName
          }?`,
          confirmText: 'Eliminar',
          cancelText: 'Cancelar',
        },
        async () => {
          if (!mountedRef.current || !quote.id) return;
          showLoading('Eliminando cotización');
          try {
            await deleteQuoteServer(organizationId, quote.id, true);
            addToast('Cotización eliminada');
            getQuotes();
          } catch (e: any) {
            console.log(e);
            addToast(e?.message || 'Error al eliminar');
          } finally {
            hideLoading();
          }
        }
      );
    },
    [
      canWriteQuotations,
      addToast,
      showConfirmation,
      organizationId,
      getQuotes,
      showLoading,
      hideLoading,
    ]
  );

  const handleConvert = useCallback(
    (quote: Quote) => {
      showConfirmation(
        {
          title: 'Convertir a venta',
          message: `¿Convertir la cotización ${getQuoteReference(
            quote
          )} en una venta? La cotización se eliminará.`,
          confirmText: 'Convertir',
          cancelText: 'Cancelar',
        },
        async () => {
          if (!mountedRef.current || !quote.id) return;
          showLoading('Convirtiendo a venta');
          try {
            const sale = await convertQuoteServer(organizationId, quote.id);
            const saleRef = sale.saleNumber
              ? `#${sale.saleNumber}`
              : sale.id?.substring(0, 8) || '';
            addToast(
              saleRef
                ? `Venta ${saleRef} creada desde cotización`
                : 'Venta creada desde cotización'
            );
            setShowQuoteView(false);
            setSelectedQuoteForView(null);
            getQuotes();
          } catch (e: any) {
            console.log(e);
            addToast(e?.message || 'Error al convertir la cotización');
          } finally {
            hideLoading();
          }
        }
      );
    },
    [
      addToast,
      showConfirmation,
      organizationId,
      getQuotes,
      showLoading,
      hideLoading,
    ]
  );

  const handleView = useCallback((quote: Quote) => {
    setSelectedQuoteForView(quote);
    setShowQuoteView(true);
  }, []);

  const handleEdit = useCallback((quote: Quote) => {
    if (!canWriteQuotations) return;
    setQuoteToEdit(quote);
    setShowForm(true);
  }, [canWriteQuotations]);

  const handleCloseForm = useCallback(() => {
    setShowForm(false);
    setQuoteToEdit(null);
  }, []);

  const loadMoreQuotes = useCallback(async () => {
    if (!mountedRef.current) return;
    setLoadMorePending(true);
    try {
      const result = await loadQuotes(
        organizationId,
        search,
        lastDocument,
        10
      );
      if (mountedRef.current) {
        setQuotes(prev => [...prev, ...result.quotes]);
        setLoadMore(result.hasMore);
        setLastDocument(result.lastDoc);
        setLoadMorePending(false);
      }
    } catch (e) {
      console.log(e);
      if (mountedRef.current) {
        setLoadMorePending(false);
        setLoadMore(false);
      }
    }
  }, [organizationId, search, lastDocument]);

  return (
    <Container fluid className="fade-in p-0 p-md-4">
      {isLoading ? <Loading /> : null}

      <SubscriptionGuard organization={organization}>
        <div className="white-card mb-4">
          <div className="text-center text-md-start">
            <h2 className="fw-bold mb-0">Cotizaciones</h2>
            <p className="text-secondary mb-0 mt-2">
              Estimaciones para clientes. Puedes convertirlas en ventas cuando
              estén listas.
            </p>
          </div>
        </div>

        <div className="white-card mb-4">
          <Row className="g-3">
            <Col xs={12} md={8}>
              <InputGroup>
                <InputGroup.Text>
                  <FiSearch />
                </InputGroup.Text>
                <FormControl
                  type="text"
                  value={search}
                  placeholder="Buscar por cliente o número…"
                  onChange={e => {
                    if (mountedRef.current) setSearch(e.currentTarget.value);
                  }}
                  onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') getQuotes();
                  }}
                />
                <Button variant="outline-secondary" onClick={getQuotes}>
                  Buscar
                </Button>
              </InputGroup>
            </Col>
            <Col xs={12} md={4}>
              <RequirePanelWrite area={PANEL_PERMISSIONS.QUOTATIONS}>
                <Button
                  variant="primary"
                  className="w-100"
                  onClick={() => {
                    setQuoteToEdit(null);
                    setShowForm(true);
                  }}
                >
                  <FiPlus className="me-1" />
                  Nueva cotización
                </Button>
              </RequirePanelWrite>
            </Col>
          </Row>
        </div>

        <PanelReadOnlyBanner area={PANEL_PERMISSIONS.QUOTATIONS} />

        {pending ? (
          <div className="d-flex justify-content-start mt-2">
            <Spinner animation="border" />
          </div>
        ) : quotes.length > 0 ? (
          <>
            <div className="d-block d-lg-none">
              {quotes.map((q, i) => (
                <Card
                  key={q.id || i}
                  className={`mb-3 border-0 shadow-sm ${listStyles.clickableCard}`}
                  onClick={() => handleView(q)}
                >
                  <Card.Body className="p-3">
                    <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
                      <div className="min-w-0">
                        <div className={styles.referenceMain}>
                          {getQuoteReference(q)}
                        </div>
                        <h6 className={`mb-1 mt-1 ${styles.primaryLine}`}>
                          {q.clientName}
                        </h6>
                        <div className={styles.secondaryLine}>
                          {renderCreatorSalesPerson(q.creatorSalesPerson)}
                        </div>
                      </div>
                      <div className={styles.totalCell}>
                        {formatMoney(q.total || 0)}
                      </div>
                    </div>
                    <div
                      className="mt-2"
                      onClick={e => e.stopPropagation()}
                    >
                      <QuotationRowActions
                        quote={q}
                        layout="mobile"
                        canWrite={canWriteQuotations}
                        onView={handleView}
                        onEdit={handleEdit}
                        onConvert={handleConvert}
                        onDelete={handleDelete}
                      />
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>

            <Card
              className={`d-none d-lg-block border-0 shadow-sm ${styles.quotationsTableCard}`}
            >
              <div className={styles.quotationsTableScroll}>
                <Table
                  hover
                  responsive
                  className={`mb-0 ${listStyles.dataTable} ${styles.quotationsTable}`}
                >
                  <thead>
                    <tr>
                      <th>Ref.</th>
                      <th>Cliente</th>
                      <th>Vendedor</th>
                      <th>Fecha</th>
                      <th className="text-end">Total</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotes.map((q, i) => (
                      <tr
                        key={q.id || i}
                        className={listStyles.clickableRow}
                        onClick={() => handleView(q)}
                      >
                        <td className={styles.referenceCell}>
                          <div className={styles.referenceMain}>
                            {getQuoteReference(q)}
                          </div>
                        </td>
                        <td className={styles.clientCell}>
                          <div className={styles.primaryLine}>
                            {q.clientName}
                          </div>
                        </td>
                        <td>
                          {renderCreatorSalesPerson(q.creatorSalesPerson)}
                        </td>
                        <td className={styles.dateCell}>
                          <div>{formatDateEsFromFirestore(q.created)}</div>
                          <div className={styles.dateTimeMeta}>
                            {formatTimeEsFromFirestore(q.created)}
                          </div>
                        </td>
                        <td className={`text-end ${styles.totalCell}`}>
                          {formatMoney(q.total || 0)}
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          <QuotationRowActions
                            quote={q}
                            canWrite={canWriteQuotations}
                            onView={handleView}
                            onEdit={handleEdit}
                            onConvert={handleConvert}
                            onDelete={handleDelete}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card>

            {loadMore && (
              <div className="d-flex justify-content-center mt-3">
                <Button
                  variant="outline-primary"
                  disabled={loadMorePending}
                  onClick={() => void loadMoreQuotes()}
                >
                  {loadMorePending ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    'Cargar más'
                  )}
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="white-card text-center py-5">
            <p className="text-secondary mb-3">No hay cotizaciones aún.</p>
            <RequirePanelWrite area={PANEL_PERMISSIONS.QUOTATIONS}>
              <Button
                variant="primary"
                onClick={() => {
                  setQuoteToEdit(null);
                  setShowForm(true);
                }}
              >
                <FiPlus className="me-1" />
                Crear primera cotización
              </Button>
            </RequirePanelWrite>
          </div>
        )}

        <QuotationForm
          show={showForm}
          onClose={handleCloseForm}
          onSaved={() => {
            getQuotes();
          }}
          organizationId={organizationId}
          addToast={addToast}
          quoteToEdit={quoteToEdit}
        />

        <QuotationView
          show={showQuoteView}
          onClose={() => {
            setShowQuoteView(false);
            setSelectedQuoteForView(null);
          }}
          quote={selectedQuoteForView}
          referenceCurrency={referenceCurrency}
          onConvert={canWriteQuotations ? handleConvert : undefined}
          onEdit={canWriteQuotations ? handleEdit : undefined}
        />
      </SubscriptionGuard>
    </Container>
  );
}
